/**
 * LOCATION: dashboard-agent/src/pages/ChatWindow.js
 * Real-time chat window — fully mobile responsive, FA icons only
 * Poll merges by ID — no blink/flash
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../services/api';

const loadFA = () => {
  if (!document.getElementById('fa-cdn-cw')) {
    const fa = document.createElement('link');
    fa.id = 'fa-cdn-cw'; fa.rel = 'stylesheet';
    fa.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css';
    document.head.appendChild(fa);
  }
};

const formatTime = (ts) => {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

const ChatWindow = () => {
  const { sessionId } = useParams();
  const navigate      = useNavigate();
  const [messages,        setMessages]        = useState([]);
  const [inputMessage,    setInputMessage]    = useState('');
  const [loading,         setLoading]         = useState(true);
  const [sending,         setSending]         = useState(false);
  const [customerTyping,  setCustomerTyping]  = useState(false);
  const [confirmClose,    setConfirmClose]    = useState(false);
  const messagesEndRef = useRef(null);
  const shownIds       = useRef(new Set());
  const textareaRef    = useRef(null);
  const agent = JSON.parse(localStorage.getItem('janimony_agent') || '{}');

  useEffect(() => { loadFA(); }, []);

  const mergeMessages = useCallback((incoming) => {
    const newOnes = incoming.filter(msg => {
      const id = msg.id || (msg.sender_type + '_' + msg.created_at);
      if (shownIds.current.has(id)) return false;
      shownIds.current.add(id);
      return true;
    });
    if (newOnes.length > 0) setMessages(prev => [...prev, ...newOnes]);
  }, []);

  const loadMessages = useCallback(async () => {
    try {
      const res = await API.getChatMessages(agent.businessId, sessionId);
      if (res.success) {
        mergeMessages(res.messages || []);
        setCustomerTyping(res.customerTyping || false);
      }
    } catch (err) { console.error('Failed to load messages:', err); }
    finally { setLoading(false); }
  }, [sessionId, agent.businessId, mergeMessages]);

  useEffect(() => {
    loadMessages();
    const interval = setInterval(loadMessages, 2500);
    return () => clearInterval(interval);
  }, [loadMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, customerTyping]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || sending) return;
    setSending(true);
    const txt    = inputMessage.trim();
    const tempId = 'temp_' + Date.now();
    setInputMessage('');
    shownIds.current.add(tempId);
    setMessages(prev => [...prev, {
      id: tempId, sender_type: 'agent', sender_id: agent.id,
      content: txt, created_at: new Date().toISOString(),
    }]);
    try {
      await API.sendAgentReply(sessionId, agent.id, txt);
    } catch {
      alert('Failed to send. Please try again.');
      setInputMessage(txt);
      setMessages(prev => prev.filter(m => m.id !== tempId));
      shownIds.current.delete(tempId);
    } finally { setSending(false); }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(e); }
  };

  const handleCloseChat = async () => {
    try {
      await API.closeSession(sessionId);
      navigate('/chats');
    } catch { console.error('Failed to close chat'); }
  };

  const senderColor = (type) => {
    if (type === 'agent')    return 'linear-gradient(135deg,#667eea,#764ba2)';
    if (type === 'ai')       return '#f0fdf4';
    return '#fff';
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        *{box-sizing:border-box;}
        .cw-page{display:flex;flex-direction:column;height:100vh;background:#f8fafc;font-family:'DM Sans',sans-serif;}

        /* Top bar */
        .cw-topbar{background:#fff;padding:0 16px;height:60px;display:flex;align-items:center;justify-content:space-between;border-bottom:1px solid #e5e7eb;box-shadow:0 1px 4px rgba(0,0,0,0.05);flex-shrink:0;gap:8px;}
        .cw-back{display:flex;align-items:center;gap:6px;padding:7px 12px;background:#f3f4f6;border:none;border-radius:9px;cursor:pointer;font-weight:700;font-size:13px;color:#374151;font-family:'DM Sans',sans-serif;}
        .cw-session-info{text-align:center;flex:1;}
        .cw-session-id{font-size:15px;font-weight:800;color:#111827;}
        .cw-session-sub{font-size:11px;color:#9ca3af;}
        .cw-close-btn{display:flex;align-items:center;gap:6px;padding:7px 14px;background:#fee2e2;color:#b91c1c;border:none;border-radius:9px;cursor:pointer;font-weight:700;font-size:13px;font-family:'DM Sans',sans-serif;}

        /* Messages area */
        .cw-messages{flex:1;overflow-y:auto;padding:16px;display:flex;flex-direction:column;gap:12px;background:#f8fafc;}
        .cw-row{display:flex;align-items:flex-end;gap:8px;}
        .cw-row.right{flex-direction:row-reverse;}
        .cw-msg-avatar{width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:11px;}
        .cw-bubble{max-width:72%;padding:10px 14px;border-radius:12px;font-size:14px;display:flex;flex-direction:column;gap:5px;}
        .cw-bubble.customer{background:#fff;border:1px solid #e5e7eb;border-radius:12px 12px 12px 2px;}
        .cw-bubble.agent{border-radius:12px 12px 2px 12px;color:#fff;}
        .cw-bubble.ai{background:#f0fdf4;border:1px solid #bbf7d0;border-radius:12px 12px 12px 2px;}
        .cw-lbl{font-size:10px;font-weight:700;opacity:0.65;text-transform:uppercase;letter-spacing:0.5px;}
        .cw-content{font-size:14px;line-height:1.55;white-space:pre-wrap;word-break:break-word;}
        .cw-time{font-size:10px;opacity:0.55;align-self:flex-end;}
        .cw-typing{display:flex;gap:4px;padding:4px 0;}
        .cw-dot{width:7px;height:7px;border-radius:50%;background:#9ca3af;animation:cw-bounce 1.3s infinite ease-in-out;}
        .cw-dot:nth-child(2){animation-delay:0.15s;}
        .cw-dot:nth-child(3){animation-delay:0.3s;}
        @keyframes cw-bounce{0%,60%,100%{transform:translateY(0);}30%{transform:translateY(-6px);}}

        /* Loading */
        .cw-loading{text-align:center;padding:40px;color:#6b7280;}
        .cw-empty{text-align:center;padding:40px;color:#9ca3af;font-size:14px;}

        /* Input area */
        .cw-input-area{background:#fff;padding:12px 16px;border-top:1px solid #e5e7eb;flex-shrink:0;}
        .cw-input-wrap{display:flex;gap:10px;align-items:flex-end;}
        .cw-textarea{flex:1;padding:11px 14px;border:2px solid #e5e7eb;border-radius:12px;font-size:14px;outline:none;resize:none;font-family:'DM Sans',sans-serif;line-height:1.5;max-height:120px;transition:border-color 0.2s;}
        .cw-textarea:focus{border-color:#667eea;}
        .cw-send{width:44px;height:44px;background:linear-gradient(135deg,#667eea,#764ba2);color:#fff;border:none;border-radius:12px;cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:all 0.2s;box-shadow:0 4px 10px rgba(102,126,234,0.3);}
        .cw-send:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 6px 14px rgba(102,126,234,0.4);}
        .cw-send:disabled{opacity:0.4;cursor:not-allowed;}
        .cw-input-hint{font-size:11px;color:#9ca3af;margin-top:6px;display:flex;align-items:center;gap:4px;}

        /* Confirm close overlay */
        .cw-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.45);display:flex;align-items:center;justify-content:center;z-index:100;padding:20px;}
        .cw-confirm{background:#fff;border-radius:16px;padding:28px 24px;max-width:360px;width:100%;box-shadow:0 20px 50px rgba(0,0,0,0.25);text-align:center;}
        .cw-confirm-icon{width:52px;height:52px;background:#fee2e2;border-radius:14px;display:flex;align-items:center;justify-content:center;margin:0 auto 16px;}
        .cw-confirm-title{font-size:18px;font-weight:800;color:#111827;margin:0 0 8px;}
        .cw-confirm-sub{font-size:14px;color:#6b7280;line-height:1.6;margin:0 0 22px;}
        .cw-confirm-btns{display:flex;gap:10px;}
        .cw-confirm-cancel{flex:1;padding:11px;background:#f3f4f6;border:none;border-radius:10px;cursor:pointer;font-weight:600;font-size:14px;font-family:'DM Sans',sans-serif;color:#374151;}
        .cw-confirm-close{flex:1;padding:11px;background:#ef4444;color:#fff;border:none;border-radius:10px;cursor:pointer;font-weight:700;font-size:14px;font-family:'DM Sans',sans-serif;}

        @media(max-width:480px){
          .cw-close-btn span{display:none;}
          .cw-back span{display:none;}
          .cw-session-id{font-size:13px;}
          .cw-bubble{max-width:85%;}
          .cw-messages{padding:12px;}
        }
      `}</style>

      <div className="cw-page">

        {/* Top bar */}
        <div className="cw-topbar">
          <button className="cw-back" onClick={() => navigate('/chats')}>
            <i className="fa-solid fa-arrow-left"></i>
            <span>Back</span>
          </button>

          <div className="cw-session-info">
            <div className="cw-session-id">
              <i className="fa-solid fa-comment-dots" style={{ color: '#667eea', marginRight: 6, fontSize: 13 }}></i>
              Chat #{sessionId.substring(0, 8)}
            </div>
            <div className="cw-session-sub">Agent: {agent.name}</div>
          </div>

          <button className="cw-close-btn" onClick={() => setConfirmClose(true)}>
            <i className="fa-solid fa-xmark"></i>
            <span>Close Chat</span>
          </button>
        </div>

        {/* Messages */}
        <div className="cw-messages">
          {loading ? (
            <div className="cw-loading">
              <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: 24, display: 'block', marginBottom: 10, color: '#667eea' }}></i>
              Loading messages...
            </div>
          ) : messages.length === 0 ? (
            <div className="cw-empty">
              <i className="fa-solid fa-comment-dots" style={{ fontSize: 40, opacity: 0.2, display: 'block', marginBottom: 10, color: '#667eea' }}></i>
              No messages yet — wait for the customer
            </div>
          ) : (
            <>
              {messages.map((msg, idx) => {
                const isAgent    = msg.sender_type === 'agent';
                const isCustomer = msg.sender_type === 'customer';
                const isAI       = msg.sender_type === 'ai';

                return (
                  <div key={msg.id || idx} className={`cw-row${isAgent ? ' right' : ''}`}>
                    {/* Avatar */}
                    <div className="cw-msg-avatar" style={{
                      background: isAgent ? 'linear-gradient(135deg,#667eea,#764ba2)'
                        : isAI ? '#bbf7d0' : '#e5e7eb',
                    }}>
                      <i
                        className={`fa-solid ${isAgent ? 'fa-headset' : isAI ? 'fa-robot' : 'fa-user'}`}
                        style={{ color: isAgent ? '#fff' : isAI ? '#16a34a' : '#9ca3af', fontSize: 12 }}
                      ></i>
                    </div>

                    {/* Bubble */}
                    <div
                      className={`cw-bubble ${msg.sender_type}`}
                      style={{ background: isAgent ? senderColor('agent') : isAI ? '#f0fdf4' : '#fff' }}
                    >
                      <span className="cw-lbl" style={{ color: isAgent ? 'rgba(255,255,255,0.7)' : '#9ca3af' }}>
                        {isCustomer ? 'Customer' : isAI ? 'AI Assistant' : agent.name}
                      </span>
                      <span className="cw-content" style={{ color: isAgent ? '#fff' : '#111827' }}>
                        {msg.content}
                      </span>
                      <span className="cw-time" style={{ color: isAgent ? 'rgba(255,255,255,0.5)' : '#9ca3af' }}>
                        {formatTime(msg.created_at)}
                      </span>
                    </div>
                  </div>
                );
              })}

              {/* Typing indicator */}
              {customerTyping && (
                <div className="cw-row">
                  <div className="cw-msg-avatar" style={{ background: '#e5e7eb' }}>
                    <i className="fa-solid fa-user" style={{ color: '#9ca3af', fontSize: 12 }}></i>
                  </div>
                  <div className="cw-bubble customer">
                    <span className="cw-lbl" style={{ color: '#9ca3af' }}>Customer</span>
                    <div className="cw-typing">
                      <div className="cw-dot"></div>
                      <div className="cw-dot"></div>
                      <div className="cw-dot"></div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input area */}
        <div className="cw-input-area">
          <form onSubmit={sendMessage} className="cw-input-wrap">
            <textarea
              ref={textareaRef}
              value={inputMessage}
              onChange={e => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your reply…"
              className="cw-textarea"
              rows={2}
              disabled={sending}
            />
            <button
              type="submit"
              className="cw-send"
              disabled={!inputMessage.trim() || sending}
            >
              {sending
                ? <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: 16 }}></i>
                : <i className="fa-solid fa-paper-plane" style={{ fontSize: 16 }}></i>}
            </button>
          </form>
          <p className="cw-input-hint">
            <i className="fa-solid fa-circle-info"></i>
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>

        {/* Confirm close overlay */}
        {confirmClose && (
          <div className="cw-overlay" onClick={() => setConfirmClose(false)}>
            <div className="cw-confirm" onClick={e => e.stopPropagation()}>
              <div className="cw-confirm-icon">
                <i className="fa-solid fa-xmark" style={{ color: '#ef4444', fontSize: 22 }}></i>
              </div>
              <h3 className="cw-confirm-title">Close this chat?</h3>
              <p className="cw-confirm-sub">
                The customer will be notified that the session has ended. This action cannot be undone.
              </p>
              <div className="cw-confirm-btns">
                <button className="cw-confirm-cancel" onClick={() => setConfirmClose(false)}>
                  Cancel
                </button>
                <button className="cw-confirm-close" onClick={handleCloseChat}>
                  <i className="fa-solid fa-xmark" style={{ marginRight: 6 }}></i>
                  Close Chat
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );
};

export default ChatWindow;